"use client"

import { useState, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faWallet,
    faMoneyBillWave,
    faChartPie,
    faPlus,
    faTrash,
    faEdit,
    faSave,
    faTimes,
    faArrowUp,
    faArrowDown,
    faHistory,
} from "@fortawesome/free-solid-svg-icons"

const STORAGE_KEY = "budget_data"

const getDefaultData = () => ({
    monthlyBudget: 0,
    expenses: [],
    currentMonth: new Date().toISOString().slice(0, 7),
})

const loadData = () => {
    try {
        const data = localStorage.getItem(STORAGE_KEY)
        if (data) {
            return JSON.parse(data)
        }
    } catch (error) {
        console.error("Error loading budget data:", error)
    }
    return getDefaultData()
}

const saveData = (data) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (error) {
        console.error("Error saving budget data:", error)
    }
}

const categories = [
    { id: "food", name: "Food & Dining", color: "#28a745" },
    { id: "transport", name: "Transportation", color: "#007bff" },
    { id: "utilities", name: "Utilities", color: "#ffc107" },
    { id: "entertainment", name: "Entertainment", color: "#dc3545" },
    { id: "shopping", name: "Shopping", color: "#6f42c1" },
    { id: "healthcare", name: "Healthcare", color: "#17a2b8" },
    { id: "education", name: "Education", color: "#fd7e14" },
    { id: "other", name: "Other", color: "#6c757d" },
]

export default function Budget() {
    const [budgetData, setBudgetData] = useState(getDefaultData())
    const [showAddExpense, setShowAddExpense] = useState(false)
    const [showSetBudget, setShowSetBudget] = useState(false)
    const [editingExpense, setEditingExpense] = useState(null)
    const [newExpense, setNewExpense] = useState({
        description: "",
        amount: "",
        category: "food",
        date: new Date().toISOString().split("T")[0],
    })
    const [newBudget, setNewBudget] = useState("")
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7))

    useEffect(() => {
        const data = loadData()
        setBudgetData(data)
        setNewBudget(data.monthlyBudget.toString())
    }, [])

    useEffect(() => {
        saveData(budgetData)
    }, [budgetData])

    // Filter expenses by selected month
    const filteredExpenses = budgetData.expenses.filter((expense) => expense.date.startsWith(selectedMonth))

    // Calculate totals
    const totalSpent = filteredExpenses.reduce((sum, exp) => sum + Number.parseFloat(exp.amount), 0)
    const remaining = budgetData.monthlyBudget - totalSpent
    const percentageUsed = budgetData.monthlyBudget > 0 ? (totalSpent / budgetData.monthlyBudget) * 100 : 0

    // Category breakdown
    const categoryBreakdown = categories
        .map((cat) => {
            const catTotal = filteredExpenses
                .filter((exp) => exp.category === cat.id)
                .reduce((sum, exp) => sum + Number.parseFloat(exp.amount), 0)
            return {
                ...cat,
                total: catTotal,
                percentage: totalSpent > 0 ? (catTotal / totalSpent) * 100 : 0,
            }
        })
        .filter((cat) => cat.total > 0)

    const handleSetBudget = () => {
        const amount = Number.parseFloat(newBudget)
        if (!isNaN(amount) && amount >= 0) {
            setBudgetData((prev) => ({
                ...prev,
                monthlyBudget: amount,
            }))
            setShowSetBudget(false)
        }
    }

    const handleAddExpense = () => {
        if (newExpense.description && newExpense.amount && Number.parseFloat(newExpense.amount) > 0) {
            const expense = {
                id: Date.now(),
                ...newExpense,
                amount: Number.parseFloat(newExpense.amount),
                createdAt: new Date().toISOString(),
            }
            setBudgetData((prev) => ({
                ...prev,
                expenses: [expense, ...prev.expenses],
            }))
            setNewExpense({
                description: "",
                amount: "",
                category: "food",
                date: new Date().toISOString().split("T")[0],
            })
            setShowAddExpense(false)
        }
    }

    const handleEditExpense = (expense) => {
        setEditingExpense(expense)
        setNewExpense({
            description: expense.description,
            amount: expense.amount.toString(),
            category: expense.category,
            date: expense.date,
        })
        setShowAddExpense(true)
    }

    const handleUpdateExpense = () => {
        if (newExpense.description && newExpense.amount && Number.parseFloat(newExpense.amount) > 0) {
            setBudgetData((prev) => ({
                ...prev,
                expenses: prev.expenses.map((exp) =>
                    exp.id === editingExpense.id
                        ? {
                            ...exp,
                            description: newExpense.description,
                            amount: Number.parseFloat(newExpense.amount),
                            category: newExpense.category,
                            date: newExpense.date,
                        }
                        : exp,
                ),
            }))
            setEditingExpense(null)
            setNewExpense({
                description: "",
                amount: "",
                category: "food",
                date: new Date().toISOString().split("T")[0],
            })
            setShowAddExpense(false)
        }
    }

    const handleDeleteExpense = (id) => {
        if (window.confirm("Are you sure you want to delete this expense?")) {
            setBudgetData((prev) => ({
                ...prev,
                expenses: prev.expenses.filter((exp) => exp.id !== id),
            }))
        }
    }

    const cancelEdit = () => {
        setEditingExpense(null)
        setNewExpense({
            description: "",
            amount: "",
            category: "food",
            date: new Date().toISOString().split("T")[0],
        })
        setShowAddExpense(false)
    }

    const getCategoryColor = (categoryId) => {
        const cat = categories.find((c) => c.id === categoryId)
        return cat ? cat.color : "#6c757d"
    }

    const getCategoryName = (categoryId) => {
        const cat = categories.find((c) => c.id === categoryId)
        return cat ? cat.name : "Other"
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount)
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
        })
    }

    return (
        <section className="main-sec">
            <div className="container-fluid">
                {/* Header */}
                <div className="row">
                    <div className="col-lg-12">
                        <div className="dashboard-header mb-4">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                <div>
                                    <h4 className="dash-head mb-1 text-white">
                                        <FontAwesomeIcon icon={faWallet} className="me-2" />
                                        Budget Management
                                    </h4>
                                    <p className="mb-0 text-dark fw-bold">Track your monthly budget and expenses</p>
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    <input
                                        type="month"
                                        className="form-control"
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(e.target.value)}
                                        style={{ width: "auto" }}
                                    />
                                    <button className="btn btn-light" onClick={() => setShowSetBudget(true)}>
                                        <FontAwesomeIcon icon={faEdit} className="me-1" />
                                        Set Budget
                                    </button>
                                    <button
                                        className="btn btn-warning"
                                        onClick={() => {
                                            setEditingExpense(null)
                                            setNewExpense({
                                                description: "",
                                                amount: "",
                                                category: "food",
                                                date: new Date().toISOString().split("T")[0],
                                            })
                                            setShowAddExpense(true)
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                                        Add Expense
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="row mb-4">
                    <div className="col-lg-12">
                        <h5 className="mb-3">Budget Overview</h5>
                    </div>
                    <div className="col-lg-12">
                        <div className="summary-cards-grid">
                            <div className="summary-card total-revenue">
                                <div className="summary-icon">
                                    <FontAwesomeIcon icon={faWallet} />
                                </div>
                                <div className="summary-content">
                                    <h6 className="text-white">Monthly Budget</h6>
                                    <h4 className="text-white">{formatCurrency(budgetData.monthlyBudget)}</h4>
                                </div>
                            </div>
                            <div className="summary-card total-commission">
                                <div className="summary-icon">
                                    <FontAwesomeIcon icon={faArrowDown} />
                                </div>
                                <div className="summary-content">
                                    <h6 className="text-white">Total Spent</h6>
                                    <h4 className="text-white">{formatCurrency(totalSpent)}</h4>
                                </div>
                            </div>
                            <div className={`summary-card ${remaining >= 0 ? "remaining-amount" : "payment-status"}`}>
                                <div className="summary-icon">
                                    <FontAwesomeIcon icon={remaining >= 0 ? faArrowUp : faArrowDown} />
                                </div>
                                <div className="summary-content">
                                    <h6 className="text-white">{remaining >= 0 ? "Remaining" : "Over Budget"}</h6>
                                    <h4 className="text-white">{formatCurrency(Math.abs(remaining))}</h4>
                                </div>
                            </div>
                            <div className="summary-card payment-status">
                                <div className="summary-icon">
                                    <FontAwesomeIcon icon={faChartPie} />
                                </div>
                                <div className="summary-content">
                                    <h6 className="text-white">Budget Used</h6>
                                    <h4 className="text-white">{percentageUsed.toFixed(1)}%</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="row mb-4">
                    <div className="col-lg-12">
                        <div className="cards">
                            <h6 className="mb-3">Budget Progress</h6>
                            <div className="progress" style={{ height: "25px" }}>
                                <div
                                    className={`progress-bar ${percentageUsed > 100 ? "bg-danger" : percentageUsed > 75 ? "bg-warning" : "bg-success"
                                        }`}
                                    role="progressbar"
                                    style={{ width: `${Math.min(percentageUsed, 100)}%` }}
                                >
                                    {percentageUsed.toFixed(1)}%
                                </div>
                            </div>
                            <div className="d-flex justify-content-between mt-2">
                                <small className="text-muted">Spent: {formatCurrency(totalSpent)}</small>
                                <small className="text-muted">Budget: {formatCurrency(budgetData.monthlyBudget)}</small>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* Category Breakdown */}
                    <div className="col-lg-4 mb-4">
                        <div className="cards" style={{ height: "100%" }}>
                            <h6 className="mb-3">
                                <FontAwesomeIcon icon={faChartPie} className="me-2" />
                                Category Breakdown
                            </h6>
                            {categoryBreakdown.length > 0 ? (
                                <div className="category-list">
                                    {categoryBreakdown.map((cat) => (
                                        <div key={cat.id} className="category-item mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <div className="d-flex align-items-center">
                                                    <span className="category-dot me-2" style={{ backgroundColor: cat.color }}></span>
                                                    <span>{cat.name}</span>
                                                </div>
                                                <span className="fw-bold">{formatCurrency(cat.total)}</span>
                                            </div>
                                            <div className="progress" style={{ height: "8px" }}>
                                                <div
                                                    className="progress-bar"
                                                    style={{
                                                        width: `${cat.percentage}%`,
                                                        backgroundColor: cat.color,
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center text-muted py-4">
                                    <FontAwesomeIcon icon={faChartPie} size="3x" className="mb-3 opacity-50" />
                                    <p>No expenses recorded for this month</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Expense List */}
                    <div className="col-lg-8 mb-4">
                        <div className="cards">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">
                                    <FontAwesomeIcon icon={faHistory} className="me-2" />
                                    Recent Expenses
                                </h6>
                                <span className="badge bg-secondary">{filteredExpenses.length} items</span>
                            </div>
                            {filteredExpenses.length > 0 ? (
                                <div className="expense-list">
                                    {filteredExpenses.map((expense) => (
                                        <div key={expense.id} className="expense-item">
                                            <div className="d-flex align-items-center">
                                                <span
                                                    className="category-badge me-3"
                                                    style={{
                                                        backgroundColor: getCategoryColor(expense.category),
                                                    }}
                                                >
                                                    {getCategoryName(expense.category).charAt(0)}
                                                </span>
                                                <div className="expense-details">
                                                    <h6 className="mb-0">{expense.description}</h6>
                                                    <small className="text-muted">
                                                        {getCategoryName(expense.category)} • {formatDate(expense.date)}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <span className="expense-amount me-3">{formatCurrency(expense.amount)}</span>
                                                <button
                                                    className="btn btn-sm btn-outline-primary me-1"
                                                    onClick={() => handleEditExpense(expense)}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteExpense(expense.id)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center text-muted py-5">
                                    <FontAwesomeIcon icon={faMoneyBillWave} size="3x" className="mb-3 opacity-50" />
                                    <p>No expenses recorded for this month</p>
                                    <button className="btn btn-primary" onClick={() => setShowAddExpense(true)}>
                                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                                        Add Your First Expense
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Set Budget Modal */}
            {showSetBudget && (
                <div className="modal-overlay" onClick={() => setShowSetBudget(false)}>
                    <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-custom">
                            <h5>
                                <FontAwesomeIcon icon={faWallet} className="me-2" />
                                Set Monthly Budget
                            </h5>
                            <button className="btn-close-custom" onClick={() => setShowSetBudget(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-custom">
                            <div className="mb-3">
                                <label className="form-label">Monthly Budget Amount (₹)</label>
                                <input
                                    type="number"
                                    className="form-control form-control-lg"
                                    placeholder="Enter your monthly budget"
                                    value={newBudget}
                                    onChange={(e) => setNewBudget(e.target.value)}
                                    min="0"
                                    step="100"
                                />
                            </div>
                        </div>
                        <div className="modal-footer-custom">
                            <button className="btn btn-secondary" onClick={() => setShowSetBudget(false)}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleSetBudget}>
                                <FontAwesomeIcon icon={faSave} className="me-1" />
                                Save Budget
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Expense Modal */}
            {showAddExpense && (
                <div className="modal-overlay" onClick={cancelEdit}>
                    <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-custom">
                            <h5>
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                {editingExpense ? "Edit Expense" : "Add New Expense"}
                            </h5>
                            <button className="btn-close-custom" onClick={cancelEdit}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-custom">
                            <div className="mb-3">
                                <label className="form-label">Description</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="What did you spend on?"
                                    value={newExpense.description}
                                    onChange={(e) =>
                                        setNewExpense((prev) => ({
                                            ...prev,
                                            description: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Amount (₹)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Enter amount"
                                    value={newExpense.amount}
                                    onChange={(e) =>
                                        setNewExpense((prev) => ({
                                            ...prev,
                                            amount: e.target.value,
                                        }))
                                    }
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Category</label>
                                <select
                                    className="form-select"
                                    value={newExpense.category}
                                    onChange={(e) =>
                                        setNewExpense((prev) => ({
                                            ...prev,
                                            category: e.target.value,
                                        }))
                                    }
                                >
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={newExpense.date}
                                    onChange={(e) =>
                                        setNewExpense((prev) => ({
                                            ...prev,
                                            date: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div className="modal-footer-custom">
                            <button className="btn btn-secondary" onClick={cancelEdit}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={editingExpense ? handleUpdateExpense : handleAddExpense}>
                                <FontAwesomeIcon icon={faSave} className="me-1" />
                                {editingExpense ? "Update Expense" : "Add Expense"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        .dashboard-header {
          background: #26a084;
          color: white;
          padding: 25px;
          border-radius: 12px;
        }

        .summary-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .summary-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 1.25rem;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          transition: transform 0.2s ease;
        }

        .summary-card:hover {
          transform: translateY(-2px);
        }

        .summary-card.total-revenue {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
        }

        .summary-card.total-commission {
          background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
        }

        .summary-card.remaining-amount {
          background: linear-gradient(135deg, #28a745 0%, #218838 100%);
        }

        .summary-card.payment-status {
          background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
        }

        .summary-icon {
          background: rgba(255,255,255,0.2);
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
        }

        .summary-content h6 {
          margin: 0;
          font-size: 0.875rem;
          opacity: 0.9;
          font-weight: 500;
        }

        .summary-content h4 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 700;
        }

        .category-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          display: inline-block;
        }

        .category-badge {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 1.1rem;
        }

        .expense-list {
          max-height: 400px;
          overflow-y: auto;
        }

        .expense-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid #f0f0f0;
          transition: background-color 0.2s;
        }

        .expense-item:hover {
          background-color: #f8f9fa;
        }

        .expense-item:last-child {
          border-bottom: none;
        }

        .expense-details h6 {
          font-size: 0.95rem;
          color: #333;
        }

        .expense-amount {
          font-weight: 700;
          font-size: 1.1rem;
          color: #dc3545;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1050;
        }

        .modal-content-custom {
          background: white;
          border-radius: 15px;
          width: 90%;
          max-width: 500px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header-custom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e9ecef;
        }

        .modal-header-custom h5 {
          margin: 0;
          font-weight: 600;
          color: #333;
        }

        .btn-close-custom {
          background: none;
          border: none;
          font-size: 1.25rem;
          color: #6c757d;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }

        .btn-close-custom:hover {
          color: #333;
        }

        .modal-body-custom {
          padding: 1.5rem;
        }

        .modal-footer-custom {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid #e9ecef;
        }

        .cards {
          padding: 1.5rem;
          background-color: #fff;
          border-radius: 15px;
          box-shadow: 0px 3.5px 5.5px 0px #00000005;
        }

        @media (max-width: 768px) {
          .dashboard-header .d-flex {
            flex-direction: column;
            gap: 15px;
          }
          
          .summary-cards-grid {
            grid-template-columns: 1fr 1fr;
          }

          .expense-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .expense-item > div:last-child {
            width: 100%;
            justify-content: space-between;
          }
        }
      `}</style>
        </section>
    )
}
